import { useState, useEffect } from "react";
import axios from "axios";
import { Save, Plus, Trash2, Code, BarChart3, Search, Tag, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

export default function AdminGoogleIntegrations() {
  const [settings, setSettings] = useState({
    analytics_id: "",
    analytics_code: "",
    adsense_client_id: "",
    adsense_slots: {},
    tag_manager_id: "",
    search_console_verification: "",
    is_active: true,
  });
  const [adSlots, setAdSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New ad slot form
  const [newSlot, setNewSlot] = useState({
    name: "",
    slot_id: "",
    format: "auto",
    position: ""
  });

  useEffect(() => {
    fetchSettings();
    fetchAdSlots();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/google`, {
        headers: getAuthHeaders(),
      });
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdSlots = async () => {
    try {
      const response = await axios.get(`${API}/settings/ad-slots`, {
        headers: getAuthHeaders(),
      });
      setAdSlots(response.data || []);
    } catch (error) {
      console.error("Error fetching ad slots:", error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/google`, settings, {
        headers: getAuthHeaders(),
      });
      toast.success("Google ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAdSlot = async () => {
    if (!newSlot.name || !newSlot.slot_id) {
      toast.error("Slot adı ve ID gerekli");
      return;
    }
    try {
      await axios.post(
        `${API}/settings/ad-slots?name=${newSlot.name}&slot_id=${newSlot.slot_id}&format=${newSlot.format}&position=${newSlot.position}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Reklam alanı eklendi");
      fetchAdSlots();
      setNewSlot({ name: "", slot_id: "", format: "auto", position: "" });
    } catch (error) {
      toast.error("Reklam alanı eklenemedi");
    }
  };

  const handleDeleteAdSlot = async (slotId) => {
    if (!window.confirm("Bu reklam alanını silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/settings/ad-slots/${slotId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Reklam alanı silindi");
      fetchAdSlots();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Google Entegrasyonları">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Google Entegrasyonları">
      <div className="space-y-6" data-testid="admin-google-integrations">
        <div className="flex items-center justify-between">
          <p className="text-slate-600">
            Google Analytics, AdSense, Tag Manager ve Search Console ayarlarını buradan yönetin.
          </p>
          <div className="flex items-center gap-2">
            <Switch
              checked={settings.is_active}
              onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
            />
            <Label>Entegrasyonlar Aktif</Label>
          </div>
        </div>

        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="adsense" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              AdSense
            </TabsTrigger>
            <TabsTrigger value="tagmanager" className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Tag Manager
            </TabsTrigger>
            <TabsTrigger value="searchconsole" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Search Console
            </TabsTrigger>
          </TabsList>

          {/* Google Analytics */}
          <TabsContent value="analytics">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-orange-500" />
                  Google Analytics
                </CardTitle>
                <CardDescription>
                  Site trafiğini ve kullanıcı davranışlarını izlemek için Google Analytics kodlarını ekleyin.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Ölçüm ID (G-XXXXXXXXXX)</Label>
                  <Input
                    value={settings.analytics_id}
                    onChange={(e) => setSettings({ ...settings, analytics_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="text-xs text-slate-500">
                    Google Analytics 4 Ölçüm ID'nizi girin (G- ile başlar)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Tam Analytics Kodu (Opsiyonel)</Label>
                  <Textarea
                    value={settings.analytics_code}
                    onChange={(e) => setSettings({ ...settings, analytics_code: e.target.value })}
                    placeholder="<!-- Google tag (gtag.js) -->..."
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">
                    Özel yapılandırma için tam script kodunu yapıştırabilirsiniz
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Google AdSense */}
          <TabsContent value="adsense">
            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-blue-500" />
                    Google AdSense
                  </CardTitle>
                  <CardDescription>
                    Sitenizde reklam göstermek için AdSense hesabınızı bağlayın ve reklam alanlarını tanımlayın.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>AdSense Yayıncı ID (ca-pub-XXXXXXXX)</Label>
                    <Input
                      value={settings.adsense_client_id}
                      onChange={(e) => setSettings({ ...settings, adsense_client_id: e.target.value })}
                      placeholder="ca-pub-1234567890123456"
                    />
                    <p className="text-xs text-slate-500">
                      AdSense hesabınızın yayıncı ID'sini girin
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Ad Slots */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle>Reklam Alanları</CardTitle>
                  <CardDescription>
                    Sitede gösterilecek reklam alanlarını tanımlayın
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Existing Slots */}
                  {adSlots.length > 0 && (
                    <div className="space-y-2">
                      {adSlots.map((slot) => (
                        <div key={slot.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{slot.name}</p>
                            <p className="text-sm text-slate-500">
                              Slot ID: {slot.slot_id} | Format: {slot.format}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteAdSlot(slot.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Slot */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Yeni Reklam Alanı Ekle</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Alan Adı</Label>
                        <Select value={newSlot.name} onValueChange={(v) => setNewSlot({ ...newSlot, name: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Konum seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="header">Header (Üst)</SelectItem>
                            <SelectItem value="sidebar">Sidebar (Yan)</SelectItem>
                            <SelectItem value="in-content">İçerik Arası</SelectItem>
                            <SelectItem value="footer">Footer (Alt)</SelectItem>
                            <SelectItem value="category-top">Kategori Üstü</SelectItem>
                            <SelectItem value="tariff-between">Tarifeler Arası</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>AdSense Slot ID</Label>
                        <Input
                          value={newSlot.slot_id}
                          onChange={(e) => setNewSlot({ ...newSlot, slot_id: e.target.value })}
                          placeholder="1234567890"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Format</Label>
                        <Select value={newSlot.format} onValueChange={(v) => setNewSlot({ ...newSlot, format: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Otomatik</SelectItem>
                            <SelectItem value="horizontal">Yatay</SelectItem>
                            <SelectItem value="vertical">Dikey</SelectItem>
                            <SelectItem value="rectangle">Dikdörtgen</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Pozisyon Açıklaması</Label>
                        <Input
                          value={newSlot.position}
                          onChange={(e) => setNewSlot({ ...newSlot, position: e.target.value })}
                          placeholder="Ör: Her 3 tarifeden sonra"
                        />
                      </div>
                    </div>
                    <Button onClick={handleAddAdSlot} className="mt-4 bg-electric-500 hover:bg-electric-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Reklam Alanı Ekle
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Google Tag Manager */}
          <TabsContent value="tagmanager">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-teal-500" />
                  Google Tag Manager
                </CardTitle>
                <CardDescription>
                  Tüm takip kodlarını merkezi olarak yönetmek için Tag Manager kullanın.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Container ID (GTM-XXXXXX)</Label>
                  <Input
                    value={settings.tag_manager_id}
                    onChange={(e) => setSettings({ ...settings, tag_manager_id: e.target.value })}
                    placeholder="GTM-XXXXXX"
                  />
                  <p className="text-xs text-slate-500">
                    Google Tag Manager Container ID'nizi girin (GTM- ile başlar)
                  </p>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Not:</strong> Tag Manager kullanıyorsanız, Analytics ve diğer takip kodlarını 
                    Tag Manager üzerinden yönetmeniz önerilir. Bu durumda yukarıdaki Analytics alanını 
                    boş bırakabilirsiniz.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Google Search Console */}
          <TabsContent value="searchconsole">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-green-500" />
                  Google Search Console
                </CardTitle>
                <CardDescription>
                  Sitenizi Google Search Console'da doğrulamak için meta tag içeriğini ekleyin.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Doğrulama Meta Tag İçeriği</Label>
                  <Input
                    value={settings.search_console_verification}
                    onChange={(e) => setSettings({ ...settings, search_console_verification: e.target.value })}
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <p className="text-xs text-slate-500">
                    Search Console'dan aldığınız meta tag içeriğini (content değerini) yapıştırın.
                    Örn: &lt;meta name="google-site-verification" content="<strong>BU_DEGERI_YAPIŞTIRIN</strong>" /&gt;
                  </p>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">Nasıl yapılır?</h4>
                  <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                    <li>Google Search Console'a gidin</li>
                    <li>Özellik ekle {">"} URL öneki seçin</li>
                    <li>HTML etiketi doğrulama yöntemini seçin</li>
                    <li>Content değerini kopyalayıp buraya yapıştırın</li>
                    <li>Ayarları kaydedin ve Search Console'da doğrulayın</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSaveSettings} 
            className="bg-electric-500 hover:bg-electric-600"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></span>
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Tüm Ayarları Kaydet
              </>
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
